import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import _ from 'lodash';
import Fuse from 'fuse.js';
import { darken, rgba } from 'polished';
import { Link, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Icon, Input } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useAuth, useFeatures } from '~/hooks';
import { colors, weights } from '~/styles';
import { useAssistant } from '../../ai/assistant/AssistantContext';

const S = {
  Overlay: styled.div`
    position: fixed;
    inset: 0;
    background: ${rgba(colors.black, 0.4)};
    z-index: 200;
  `,

  MenuCard: styled.div`
    position: fixed;
    top: 0.3125rem;
    left: 50%;
    transform: translateX(-50%);
    width: 36.1875rem;
    max-height: calc(100vh - 0.625rem);
    background: ${colors.white};
    border-radius: 0.625rem;
    box-shadow: 0 0.25rem 1.5rem ${rgba(colors.black, 0.15)};
    z-index: 201;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  `,

  SearchBarArea: styled.div`
    padding: 1.0625rem 2.125rem 1.0625rem 1.625rem;
    flex-shrink: 0;
    position: relative;
  `,

  SearchBar: styled.label`
    display: flex;
    align-items: center;
    height: 2rem;
    border: 1px solid ${colors.primary};
    border-radius: 2.75rem;
    background: ${colors.white};
    overflow: clip;
    cursor: text;
  `,

  SearchInput: styled(Input)`
    && {
      flex: 1;
      height: 100%;
      width: auto;
      max-width: none;
      padding: 0 0 0 0.875rem;
      border: none;
      border-radius: 0;
      font-size: 0.75rem;
      color: ${colors.black};
      background: transparent;

      &:focus {
        border-color: transparent;
      }

      &::placeholder {
        color: ${colors.grey40};
        font-style: italic;
      }
    }
  `,

  SearchIcon: styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    min-width: 2rem;
    color: ${colors.primary};
    font-size: 0.875rem;
    pointer-events: none;
  `,

  ClearButton: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5rem;
    height: 1.5rem;
    min-width: 1.5rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    color: ${colors.grey40};
    font-size: 0.625rem;
    font-weight: normal;
    cursor: pointer;

    &:hover {
      color: ${colors.grey75};
      background: transparent;
    }
  `,

  CloseButton: styled.button`
    position: absolute;
    top: 1.5rem;
    right: 0.6875rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    border: none;
    border-radius: 0.125rem;
    background: transparent;
    color: ${colors.danger};
    font-size: 0.625rem;
    font-weight: normal;
    cursor: pointer;

    &:hover {
      color: ${darken(0.1, colors.danger)};
      background: transparent;
    }
  `,

  Divider: styled.div`
    height: 1px;
    background: ${colors.grey10};
    flex-shrink: 0;
  `,

  ScrollArea: styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
  `,

  SectionHeader: styled.div`
    font-size: 0.75rem;
    font-weight: ${weights.black};
    color: ${colors.grey40};
    letter-spacing: 0.0375rem;
    text-transform: uppercase;
    padding: 1.4375rem 1.875rem 0;
  `,

  FilterContainer: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.375rem 1.875rem 1.875rem;
  `,

  FilterBadge: styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 1.5625rem;
    padding: 0 0.625rem;
    border: none;
    border-radius: 1.875rem;
    background: ${({ $active }) => ($active ? colors.primary : colors.grey75)};
    color: ${colors.white};
    font-size: 0.75rem;
    font-weight: ${weights.medium};
    line-height: 1;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: ${({ $active }) => ($active ? colors.accent : colors.grey55)};
      color: ${colors.white};
    }
  `,

  ItemsContainer: styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    padding: ${({ $pt, $pb }) => `${$pt || '0'} 1.25rem ${$pb || '0'}`};
  `,

  RecentPageItem: styled(Link)`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.4375rem 0.625rem;
    border-radius: 0.125rem;
    text-decoration: none;
    cursor: pointer;
    background: ${({ $selected }) => ($selected ? colors.grey5 : 'transparent')};

    &:hover {
      background: ${colors.grey5};
    }
  `,

  RecentPageTitle: styled.span`
    font-size: 0.75rem;
    font-weight: ${weights.bold};
    color: ${colors.black};
    line-height: 1;
  `,

  RecentPageSubtitle: styled.span`
    font-size: 0.75rem;
    font-weight: ${weights.normal};
    color: ${colors.grey75};
    line-height: 1;
  `,

  ResultItem: styled(Link)`
    display: flex;
    align-items: center;
    padding: 0.625rem;
    border-radius: 0.125rem;
    text-decoration: none;
    font-size: 0.75rem;
    color: ${colors.grey75};
    line-height: 1rem;
    cursor: pointer;
    background: ${({ $selected }) => ($selected ? colors.grey5 : 'transparent')};

    &:hover {
      background: ${colors.grey5};
      color: ${colors.grey75};
    }
  `,

  ResultBold: styled.span`
    font-weight: ${weights.bold};
  `,

  ResultSeparator: styled.span`
    margin: 0 0.375rem;
    opacity: 0.6;
  `,

  AIBanner: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 1.875rem;
    padding: 0 0.9375rem;
    background: ${rgba(colors.primary, 0.08)};
    flex-shrink: 0;
    border-radius: 0 0 0.625rem 0.625rem;
  `,

  AIBannerText: styled.span`
    font-size: 0.75rem;
    font-weight: ${weights.normal};
    color: ${colors.grey75};
  `,

  AIBannerLink: styled.button`
    display: flex;
    align-items: center;
    gap: 0.3125rem;
    height: auto;
    padding: 0;
    border: none;
    border-radius: 0;
    background: transparent;
    color: ${colors.primary};
    font-size: 0.75rem;
    font-weight: ${weights.bold};
    cursor: pointer;

    .icon {
      font-size: 0.5rem;
    }

    &:hover {
      background: transparent;
      color: ${colors.accent};
    }
  `,

  NoResults: styled.div`
    padding: 1.25rem 1.875rem;
    font-size: 0.75rem;
    color: ${colors.grey40};
  `,
};

const entityTypes = [
  { id: 'client', name: 'Clients' },
  { id: 'project', name: 'Projects' },
  { id: 'task', name: 'Tasks' },
];

function buildResultUrl(entity, entityTypeId, workspaceKey) {
  switch (entityTypeId) {
    case 'client':
      return `/app/${workspaceKey}/portfolio/clients/${entity.client.key}`;
    case 'project':
      return `/app/${workspaceKey}/portfolio/projects/${entity.project.client.key}/${entity.project.key}`;
    case 'task':
      return `/app/${workspaceKey}/portfolio/projects/${entity.task.project.client.key}/${entity.task.project.key}/tasks/${entity.task.number}/details`;
    default:
      return null;
  }
}

const MAX_STORED = 100;
const MAX_DISPLAYED = 10;

function getRecentKey(workspaceId) {
  return `ruddr:recentSearches:${workspaceId}`;
}

function isValidEntry(entry) {
  return (
    entry &&
    typeof entry.url === 'string' &&
    typeof entry.typeId === 'string' &&
    typeof entry.title === 'string' &&
    typeof entry.last === 'string' &&
    Array.isArray(entry.breadcrumb) &&
    entry.breadcrumb.every((s) => typeof s === 'string')
  );
}

function loadRecent(workspaceId) {
  try {
    const raw = localStorage.getItem(getRecentKey(workspaceId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const valid = parsed.filter(isValidEntry);
    if (valid.length !== parsed.length) {
      saveRecent(workspaceId, valid);
    }
    return valid;
  } catch {
    localStorage.removeItem(getRecentKey(workspaceId));
    return [];
  }
}

function saveRecent(workspaceId, items) {
  try {
    localStorage.setItem(getRecentKey(workspaceId), JSON.stringify(items));
  } catch {
    // localStorage full or unavailable
  }
}

function buildRecentEntry(entity, entityTypeId, workspaceKey) {
  const url = buildResultUrl(entity, entityTypeId, workspaceKey);
  const typeName = entityTypes.find((t) => t.id === entityTypeId)?.name;
  switch (entityTypeId) {
    case 'client':
      return { url, typeId: entityTypeId, title: typeName, breadcrumb: [entity.client.name], last: entity.client.name };
    case 'project':
      return {
        url,
        typeId: entityTypeId,
        title: typeName,
        breadcrumb: [entity.project.client.name, entity.project.name],
        last: entity.project.name,
      };
    case 'task':
      return {
        url,
        typeId: entityTypeId,
        title: typeName,
        breadcrumb: [entity.task.project.client.name, entity.task.project.name, entity.task.name],
        last: entity.task.name,
      };
    default:
      return null;
  }
}

const Sep = () => <S.ResultSeparator>|</S.ResultSeparator>;

function ResultContent({ entity, entityTypeId }) {
  switch (entityTypeId) {
    case 'client':
      return <S.ResultBold>{entity.client.name}</S.ResultBold>;
    case 'project':
      return (
        <>
          {entity.project.client.name}
          <Sep />
          <S.ResultBold>{entity.project.name}</S.ResultBold>
        </>
      );
    case 'task':
      return (
        <>
          {entity.task.project.client.name}
          <Sep />
          {entity.task.project.name}
          <Sep />
          <S.ResultBold>{entity.task.name}</S.ResultBold>
        </>
      );
    default:
      return null;
  }
}

export default function WorkspaceSearch({ onClose, onEnableAssistant }) {
  const api = useApi();
  const history = useHistory();
  const auth = useAuth();
  const features = useFeatures();
  const { workspace } = useWorkspace();
  const { openPanel } = useAssistant();

  const isBillingLocked = !workspace?.billingStatus?.isValid;
  const isDisabled = workspace?.statusId === 'disabled';
  const isAdmin = auth.workspace.manage;
  const assistantEnabled = workspace.member.assistantEnabled;
  const showAIBanner =
    !isBillingLocked && !isDisabled && ((features.assistant && assistantEnabled) || (!features.assistant && isAdmin));

  const [query, setQuery] = useState('');
  const [allRecent, setAllRecent] = useState(() => loadRecent(workspace.id));
  const queryRef = useRef(query);
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilters, setActiveFilters] = useState(new Set());
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  const recentFuse = useMemo(
    () => new Fuse(allRecent, { keys: ['title', 'last', 'breadcrumb'], threshold: 0.4 }),
    [allRecent],
  );

  const recentItems = useMemo(() => {
    let items = allRecent;
    if (activeFilters.size > 0) {
      items = items.filter((r) => activeFilters.has(r.typeId));
    }
    if (!query) return items.slice(0, MAX_DISPLAYED);
    if (!items.length) return [];
    const fuse =
      activeFilters.size > 0 ? new Fuse(items, { keys: ['title', 'last', 'breadcrumb'], threshold: 0.4 }) : recentFuse;
    return fuse.search(query, { limit: MAX_DISPLAYED }).map((r) => r.item);
  }, [query, allRecent, activeFilters, recentFuse]);

  const bumpRecent = useCallback(
    (entry) => {
      const prev = loadRecent(workspace.id);
      const filtered = prev.filter((r) => r.url !== entry.url);
      const next = [entry, ...filtered].slice(0, MAX_STORED);
      saveRecent(workspace.id, next);
      setAllRecent(next);
    },
    [workspace.id],
  );

  const recordRecent = useCallback(
    (entity, entityTypeId) => {
      const entry = buildRecentEntry(entity, entityTypeId, workspace.key);
      if (!entry) return;
      bumpRecent(entry);
    },
    [workspace.key, bumpRecent],
  );

  const isLocked = useMemo(() => workspace.statusId === 'disabled' || !workspace.billingStatus?.isValid, [workspace]);

  // Auto-focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, []);

  // Escape: clear query first, then close
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        if (queryRef.current) {
          setQuery('');
          setSelectedIndex(-1);
        } else {
          onClose();
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Fetch results
  const fetchData = useCallback(async () => {
    if (!query) {
      setResults(null);
      return;
    }

    setIsLoading(true);
    try {
      const type = activeFilters.size === 1 ? [...activeFilters][0] : undefined;
      const { data } = await api.www.workspaces(workspace.id).search().get({ query, type });

      if (query === queryRef.current) {
        // If multiple filters active, filter the results client-side
        if (activeFilters.size > 1) {
          const filtered = {};
          for (const key of activeFilters) {
            if (data[key]) filtered[key] = data[key];
          }
          setResults(filtered);
        } else {
          setResults(data);
        }
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, workspace.id, query, activeFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    queryRef.current = query;
  }, [query]);

  // Build flat list of all navigable items (recent + search results) for keyboard nav
  const flatItems = useMemo(() => {
    const items = [];
    for (const item of recentItems) {
      items.push({ kind: 'recent', item });
    }
    if (results) {
      for (const et of entityTypes) {
        if (!results[et.id]) continue;
        for (const entity of results[et.id]) {
          items.push({ kind: 'result', entity, entityTypeId: et.id });
        }
      }
    }
    return items;
  }, [recentItems, results]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, flatItems.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter' && selectedIndex >= 0 && flatItems[selectedIndex]) {
        e.preventDefault();
        const selected = flatItems[selectedIndex];
        if (selected.kind === 'recent') {
          bumpRecent(selected.item);
          history.push(selected.item.url);
          onClose();
        } else {
          const url = buildResultUrl(selected.entity, selected.entityTypeId, workspace.key);
          if (url) {
            recordRecent(selected.entity, selected.entityTypeId);
            history.push(url);
            onClose();
          }
        }
      }
    },
    [flatItems, selectedIndex, workspace.key, history, onClose, recordRecent, bumpRecent],
  );

  const handleChange = useCallback((e) => {
    setQuery(e.target.value);
    setSelectedIndex(-1);
  }, []);

  const handleToggleFilter = useCallback((filterId) => {
    setActiveFilters((prev) => {
      const next = new Set(prev);
      if (next.has(filterId)) {
        next.delete(filterId);
      } else {
        next.add(filterId);
      }
      return next;
    });
    setSelectedIndex(-1);
  }, []);

  const handleResultClick = useCallback(
    (entity, entityTypeId) => {
      if (entity && entityTypeId) {
        recordRecent(entity, entityTypeId);
      }
      onClose();
    },
    [onClose, recordRecent],
  );

  const handleAIClick = useCallback(() => {
    if (features.assistant) {
      openPanel();
      onClose();
    } else {
      onEnableAssistant();
    }
  }, [features.assistant, openPanel, onClose, onEnableAssistant]);

  const hasResults = results && _.keys(results).length > 0;
  const hasQuery = query.length > 0;

  return ReactDOM.createPortal(
    <>
      <S.Overlay onClick={onClose} />
      <S.MenuCard>
        <S.SearchBarArea>
          <S.SearchBar>
            <S.SearchInput
              ref={inputRef}
              type="search"
              placeholder="Search for..."
              wait={300}
              value={query}
              disabled={isLocked}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <S.ClearButton
                type="button"
                onClick={() => {
                  setQuery('');
                  setSelectedIndex(-1);
                  inputRef.current?.focus();
                }}>
                <Icon icon="xmark" />
              </S.ClearButton>
            )}
            <S.SearchIcon>
              <Icon icon="search" />
            </S.SearchIcon>
          </S.SearchBar>
          <S.CloseButton type="button" onClick={onClose} title="Close">
            <Icon icon="xmark" />
          </S.CloseButton>
        </S.SearchBarArea>

        <S.Divider />

        <S.ScrollArea ref={scrollRef}>
          {/* Filter badges — only when no query */}
          {!hasQuery && (
            <>
              <S.SectionHeader>Filter by</S.SectionHeader>
              <S.FilterContainer>
                {entityTypes.map((et) => (
                  <S.FilterBadge
                    key={et.id}
                    type="button"
                    $active={activeFilters.has(et.id)}
                    onClick={() => handleToggleFilter(et.id)}>
                    {et.name}
                  </S.FilterBadge>
                ))}
              </S.FilterContainer>
              <S.Divider />
            </>
          )}

          {/* Recent searches */}
          {recentItems.length > 0 && (
            <>
              <S.SectionHeader>Recent pages</S.SectionHeader>
              <S.ItemsContainer $pt="0.625rem" $pb="0.625rem">
                {recentItems.map((item, idx) => (
                  <S.RecentPageItem
                    key={item.url}
                    to={item.url}
                    onClick={() => {
                      bumpRecent(item);
                      onClose();
                    }}
                    $selected={selectedIndex === idx}>
                    <S.RecentPageTitle>{item.title}</S.RecentPageTitle>
                    <S.RecentPageSubtitle>
                      {item.breadcrumb.slice(0, -1).map((part, i) => (
                        <React.Fragment key={i}>
                          {part}
                          <Sep />
                        </React.Fragment>
                      ))}
                      <S.ResultBold>{item.last}</S.ResultBold>
                    </S.RecentPageSubtitle>
                  </S.RecentPageItem>
                ))}
              </S.ItemsContainer>
            </>
          )}

          {/* Search results */}
          {hasQuery && !isLoading && hasResults && (
            <>
              <S.Divider />
              {entityTypes.map((et) => {
                if (!results[et.id] || results[et.id].length === 0) return null;
                return (
                  <React.Fragment key={et.id}>
                    <S.SectionHeader>{et.name}</S.SectionHeader>
                    <S.ItemsContainer $pt="0.375rem" $pb="0.625rem">
                      {results[et.id].map((entity, idx) => {
                        const url = buildResultUrl(entity, et.id, workspace.key);
                        // Calculate global index offset: recentItems + prior type results
                        let globalIdx = recentItems.length;
                        for (const prev of entityTypes) {
                          if (prev.id === et.id) break;
                          if (results[prev.id]) globalIdx += results[prev.id].length;
                        }
                        globalIdx += idx;

                        return (
                          <S.ResultItem
                            key={entity[et.id].id}
                            to={url}
                            onClick={() => handleResultClick(entity, et.id)}
                            $selected={selectedIndex === globalIdx}>
                            <ResultContent entity={entity} entityTypeId={et.id} />
                          </S.ResultItem>
                        );
                      })}
                    </S.ItemsContainer>
                  </React.Fragment>
                );
              })}
            </>
          )}

          {/* No results */}
          {hasQuery && !isLoading && !hasResults && results !== null && <S.NoResults>No results found</S.NoResults>}

          {/* Loading */}
          {hasQuery && isLoading && (
            <S.NoResults>
              <Icon icon="spinner" spin />
            </S.NoResults>
          )}
        </S.ScrollArea>

        {showAIBanner && (
          <S.AIBanner>
            <S.AIBannerText>Try our new Ask Ruddr feature!</S.AIBannerText>
            <S.AIBannerLink type="button" onClick={handleAIClick}>
              Try It Now
              <Icon icon="arrow-right" />
            </S.AIBannerLink>
          </S.AIBanner>
        )}
      </S.MenuCard>
    </>,
    document.body,
  );
}
